import { useCallback, useEffect, useRef, useState } from "react";

export interface DebouncedSearchInput {
    /** Live value to bind to the input — updates synchronously on every keystroke. */
    value: string;
    /** Call from the input's onChange — updates `value` now, commits after `delayMs` of quiet. */
    onChange: (next: string) => void;
    /** Clear immediately (X button) — no debounce. */
    clear: () => void;
}

/**
 * Decouples a search input's typed value from a slower committed value (URL/query state).
 *
 * The input is controlled by local state so fast typing never loses characters to an async
 * round-trip (e.g. `setSearchParams`), while the actual search/query is committed only after
 * `delayMs` of inactivity. External resets to `committedValue` (clear elsewhere, reset filters)
 * are mirrored back into the field, but our own committed value echoing back never clobbers
 * in-flight typing.
 */
export function useDebouncedSearchInput(
    committedValue: string,
    onCommit: (value: string) => void,
    delayMs = 250,
): DebouncedSearchInput {
    const [value, setValue] = useState(committedValue);
    const lastCommittedRef = useRef(committedValue);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // Only sync when the change came from outside this hook (reset/clear elsewhere),
        // not from our own commit echoing back through the controlled `committedValue`.
        if (committedValue !== lastCommittedRef.current) {
            lastCommittedRef.current = committedValue;
            setValue(committedValue);
        }
    }, [committedValue]);

    useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        },
        [],
    );

    const commit = useCallback(
        (next: string) => {
            lastCommittedRef.current = next;
            onCommit(next);
        },
        [onCommit],
    );

    const onChange = useCallback(
        (next: string) => {
            setValue(next);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => commit(next), delayMs);
        },
        [commit, delayMs],
    );

    const clear = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setValue("");
        commit("");
    }, [commit]);

    return { value, onChange, clear };
}
